import React from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";

import { ECR_OPS_ROLE_NAMES } from "./RequireEcrOpsRole";
import { useAccessPermissions, useCurrentUserRoleNames } from "./useAccessPermissions";
import UnauthorizedAccess from "./UnauthorizedAccess";

// The entire Access Management section is scoped to platform administrators
// only — Enterprise Admin and Operations Admin — regardless of what any
// individual permission string would otherwise allow. This is a deliberate,
// explicit allow-list (not derived from auth:* permissions) per product
// decision: even a role that happens to carry auth:* should not get in here
// unless it is one of these named roles. Operations User is deliberately
// excluded — it is org-scoped only and was never meant to administer
// roles/permissions. Same two roles as RequireEcrOpsRole (Access Management
// is a section within ECR Ops) — reuses that single canonical list instead
// of keeping a second copy that could drift out of sync.
export const isAccessManagementRole = (roleNames) =>
  Array.isArray(roleNames) && roleNames.some((name) => ECR_OPS_ROLE_NAMES.includes(name));

const RequireAccessManagementRole = () => {
  const location = useLocation();
  const authToken = useSelector((state) => state.auth?.authToken);
  const { isLoading } = useAccessPermissions();
  const roleNames = useCurrentUserRoleNames();

  if (!authToken) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (isLoading || roleNames === null) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: "4rem" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!isAccessManagementRole(roleNames)) {
    return (
      <UnauthorizedAccess
        message="Access Management is restricted to Enterprise Admin and Operations Admin."
        primaryLabel="Go to OPS homepage"
        primaryTo="/ecr-ops"
      />
    );
  }

  return <Outlet />;
};

export default RequireAccessManagementRole;
